import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Stack } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Vendor } from '../../../../api/gql';
import { AresLookupButton, AresLookupResult } from '../../../../components/AresLookupButton';
import { FullSizeLoader } from '../../../../components/FullSizeLoader';
import { useVendorLookup } from '../../../../hooks/useVendorLookup';
import { VendorFormPart } from '../form-parts/VendorFormPart';
import { inputSharedProps } from '../shared';
import { InvoiceFormType } from '../types';

type Props = {
  readonly?: boolean;
};

export const ConnectedVendorForm = ({ readonly }: Props) => {
  const { control, getFieldState, getValues, formState, setValue } = useFormContext<InvoiceFormType>();
  const { t } = useTranslation();
  const [isLookedUp, setIsLookedUp] = useState(false);

  const vatIdFieldState = getFieldState('vendor.vatId', formState);

  const { isLoading, data } = useVendorLookup();

  const prefillVendorFromContact = useCallback(
    (contact: Partial<Vendor>) => {
      setValue('vendor.taxId', contact.taxId ?? '', { shouldValidate: true });
      setValue('vendor.name', contact.name ?? '', { shouldValidate: true });
      setValue('vendor.address', contact.address ?? '', {
        shouldValidate: true,
      });
    },
    [setValue],
  );

  const handleAresLookupSuccess = useCallback(
    (result: AresLookupResult) => {
      if (result.taxId) {
        setValue('vendor.taxId', result.taxId, { shouldValidate: true });
      }
      if (result.name) {
        setValue('vendor.name', result.name, { shouldValidate: true });
      }
      if (result.address) {
        setValue('vendor.address', result.address, { shouldValidate: true });
      }
    },
    [setValue],
  );

  useEffect(() => {
    if (!data) {
      return;
    }
    const vatId = getValues('vendor.vatId');
    if (!vatId) {
      return;
    }
    const lookup = data.vendors.find((vendor) => vendor.vatId === vatId);
    if (lookup && lookup.name) {
      prefillVendorFromContact(lookup);
      setIsLookedUp(true);
    }
  }, [data, getValues, prefillVendorFromContact]);

  const isContact = useMemo(
    () => isLookedUp && !vatIdFieldState.invalid && !vatIdFieldState.isDirty,
    [isLookedUp, vatIdFieldState.invalid, vatIdFieldState.isDirty],
  );

  const inputProps = {
    ...inputSharedProps,
  };

  return (
    <Stack sx={{ position: 'relative' }}>
      {isLoading ? <FullSizeLoader /> : null}
      {isContact ? (
        <Alert severity={'info'} sx={{ mt: 2 }}>
          {t('contactPrefilled')}
        </Alert>
      ) : null}
      <VendorFormPart
        readonly={readonly}
        inputProps={inputProps}
        control={control}
        slot={
          <AresLookupButton
            getVatId={() => getValues('vendor.vatId') ?? ''}
            onLookupSuccess={handleAresLookupSuccess}
          />
        }
      />
    </Stack>
  );
};
